import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useRef, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";

/**
 * One compact, keyboard-accessible control for grouped bookmark sorting/filtering.
 * Each group retains its own active value, so Conversation Bookmarks can keep a
 * chronological sort and a status filter active at the same time.
 */
export default function BookmarkSortMenu({
  groups,
  summary,
  ariaLabel,
  testId,
  filtered = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const touchGestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  const summaryText = String(summary ?? "");
  const isRelationshipSort = className.includes("bookmark-sort-menu__trigger--relationship");
  const isConversationSort = className.includes("bookmark-sort-menu__trigger--conversation");
  const structuredSummaryMatch = (isRelationshipSort || isConversationSort)
    ? summaryText.match(/^([^:]+:)(?:\s*)(.*)$/)
    : null;
  const summaryClass = summaryText.length > 27
    ? " is-very-long-summary"
    : summaryText.length > 20
      ? " is-long-summary"
      : "";

  useEffect(() => () => {
    touchGestureRef.current = null;
    suppressClickRef.current = false;
  }, []);

  const normalizedGroups = (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      ...group,
      options: (Array.isArray(group?.options) ? group.options : []).filter(
        (option) => option && option.value !== undefined && option.label,
      ),
    }))
    .filter((group) => group.label && group.options.length > 0);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`bookmark-sort-menu__trigger${filtered ? " is-filtered" : ""}${summaryClass}${className ? ` ${className}` : ""}`}
          aria-label={ariaLabel}
          data-testid={testId}
          onPointerDown={(event) => {
            if (event.pointerType !== "touch") return;
            // Radix opens on pointer-down. For touch we defer the open until
            // pointer-up so a vertical scroll gesture never triggers the menu,
            // while a genuine tap is responsive on the very first interaction.
            event.preventDefault();
            touchGestureRef.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              moved: false,
            };
          }}
          onPointerMove={(event) => {
            const gesture = touchGestureRef.current;
            if (!gesture || event.pointerId !== gesture.pointerId) return;
            if (Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY) > 8) {
              gesture.moved = true;
            }
          }}
          onPointerUp={(event) => {
            const gesture = touchGestureRef.current;
            if (!gesture || event.pointerId !== gesture.pointerId) return;
            touchGestureRef.current = null;
            suppressClickRef.current = true;
            if (!gesture.moved) setOpen((current) => !current);
            window.setTimeout(() => {
              suppressClickRef.current = false;
            }, 0);
          }}
          onPointerCancel={() => {
            touchGestureRef.current = null;
          }}
          onClick={(event) => {
            // Touch activation is handled on pointer-up above. Suppress the
            // synthetic click that follows so it cannot immediately re-toggle.
            if (suppressClickRef.current) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          <span className="bookmark-sort-menu__eyebrow">Sort by</span>
          <span className="bookmark-sort-menu__center">
            <span className="bookmark-sort-menu__summary" title={summaryText}>
              {structuredSummaryMatch ? (
                <>
                  <span className="bookmark-sort-menu__summary-prefix">{structuredSummaryMatch[1]}</span>
                  <span className="bookmark-sort-menu__summary-value">{structuredSummaryMatch[2]}</span>
                </>
              ) : summaryText}
            </span>
            {filtered ? <span className="bookmark-sort-menu__filter-dot" aria-hidden="true" /> : null}
          </span>
          <CaretDown className="bookmark-sort-menu__caret" size={13} weight="bold" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={7}
        className="bookmark-sort-menu__content"
        collisionPadding={12}
      >
        {normalizedGroups.map((group) => (
          <DropdownMenuGroup className="bookmark-sort-menu__group" key={group.label}>
            <DropdownMenuLabel className="bookmark-sort-menu__group-label">
              <span>{group.label}</span>
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={group.value} onValueChange={group.onValueChange}>
              {group.options.map((option) => (
                <DropdownMenuRadioItem
                  key={option.value}
                  value={option.value}
                  className="bookmark-sort-menu__item"
                  data-testid={option.testId}
                >
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
